// Ingredientes Page
import React, { useEffect, useState } from 'react';
import { ingredientesAPI, proveedoresAPI, alergenosAPI } from '../api/api';
import { Plus, Search, Edit, Trash2, AlertTriangle, Package, Beef, Milk, CheckCircle2 } from 'lucide-react';
import AlergenoIcon, { AlergenoList } from '../components/AlergenoIcon';

const Ingredientes = () => {
  const [ingredientes, setIngredientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [alergenos, setAlergenos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProveedor, setFilterProveedor] = useState('');
  const [filterActivo, setFilterActivo] = useState('true');
  const [showModal, setShowModal] = useState(false);
  const [editingIngrediente, setEditingIngrediente] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    proveedor_id: '',
    precio_unitario: 0,
    unidad: 'kg',
    precio_paquete: 0,
    unidades_por_paquete: 1,
    alergenos_ids: [],
    carne: false,
    origen_animal: false,
    pasteurizado: false,
    activo: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ingredientesRes, proveedoresRes, alergenosRes] = await Promise.all([
        ingredientesAPI.getAll(),
        proveedoresAPI.getAll(),
        alergenosAPI.getAll()
      ]);
      setIngredientes(Array.isArray(ingredientesRes.data) ? ingredientesRes.data : []);
      setProveedores(Array.isArray(proveedoresRes.data) ? proveedoresRes.data : []);
      setAlergenos(Array.isArray(alergenosRes.data) ? alergenosRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ensure numeric values are numbers
      const dataToSend = {
        ...formData,
        precio_unitario: parseFloat(formData.precio_unitario),
        precio_paquete: parseFloat(formData.precio_paquete),
        unidades_por_paquete: parseFloat(formData.unidades_por_paquete)
      };

      if (editingIngrediente) {
        await ingredientesAPI.update(editingIngrediente.id, dataToSend);
      } else {
        await ingredientesAPI.create(dataToSend);
      }
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving ingrediente:', error);
      alert('Error al guardar el ingrediente');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este ingrediente?')) {
      try {
        await ingredientesAPI.delete(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting ingrediente:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      proveedor_id: '',
      precio_unitario: 0,
      unidad: 'kg',
      precio_paquete: 0,
      unidades_por_paquete: 1,
      alergenos_ids: [],
      carne: false,
      origen_animal: false,
      pasteurizado: false,
      activo: true,
      tipo_iva: 10
    });
    setEditingIngrediente(null);
    setShowModal(false);
  };

  const openEditModal = (ingrediente) => {
    setEditingIngrediente(ingrediente);
    setFormData({
      nombre: ingrediente.nombre,
      proveedor_id: ingrediente.proveedor_id || '',
      precio_unitario: ingrediente.precio_unitario,
      unidad: ingrediente.unidad,
      precio_paquete: ingrediente.precio_paquete,
      unidades_por_paquete: ingrediente.unidades_por_paquete,
      alergenos_ids: ingrediente.alergenos_ids || [],
      carne: ingrediente.carne || false,
      origen_animal: ingrediente.origen_animal || false,
      pasteurizado: ingrediente.pasteurizado || false,
      activo: ingrediente.activo,
      tipo_iva: ingrediente.tipo_iva || 10
    });
    setShowModal(true);
  };

  const getProveedorName = (proveedorId) => {
    const proveedor = proveedores.find(p => p.id === proveedorId);
    return proveedor?.nombre || '-';
  };

  const getAlergenosNames = (alergenosIds) => {
    if (!alergenosIds || alergenosIds.length === 0) return '-';
    return alergenosIds.map(id => {
      const alergeno = alergenos.find(a => a.id === id);
      return alergeno ? `${alergeno.icono} ${alergeno.nombre}` : '';
    }).filter(Boolean).join(', ');
  };

  const toggleAlergeno = (alergenoId) => {
    setFormData(prev => ({
      ...prev,
      alergenos_ids: prev.alergenos_ids.includes(alergenoId)
        ? prev.alergenos_ids.filter(id => id !== alergenoId)
        : [...prev.alergenos_ids, alergenoId]
    }));
  };

  const filteredIngredientes = ingredientes.filter(ing => {
    const matchesSearch = ing.nombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProveedor = !filterProveedor || ing.proveedor_id === filterProveedor;
    const matchesActivo = filterActivo === '' || ing.activo.toString() === filterActivo;
    const matchesCategoria = ing.categoria === 'Bebida'; // Solo mostrar ingredientes de categoría Bebida
    return matchesSearch && matchesProveedor && matchesActivo && matchesCategoria;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-64">Cargando...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="bebidas-title">Bebidas</h1>
          <p className="text-gray-600 mt-1">Gestión de bebidas y líquidos</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="new-ingredient-button"
          className="bg-duetto-primary text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-opacity-90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Nuevo Ingrediente</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar ingrediente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
          </div>
          <select
            value={filterProveedor}
            onChange={(e) => setFilterProveedor(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          >
            <option value="">Todos los proveedores</option>
            {[...proveedores].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(prov => (
              <option key={prov.id} value={prov.id}>{prov.nombre}</option>
            ))}
          </select>
          <select
            value={filterActivo}
            onChange={(e) => setFilterActivo(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          >
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        <table className="w-full divide-y divide-gray-200" style={{ minWidth: '1300px' }}>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '220px' }}>Ingrediente</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '140px' }}>Proveedor</th>
              <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '140px' }}>Precio/UD (Sin IVA)</th>
              <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '160px' }}>Precio Envase (Sin IVA)</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '150px' }}>Características</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '130px' }}>Alérgenos</th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '100px' }}>Estado</th>
              <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '100px' }}>Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredIngredientes.map((ingrediente) => {
              const ingredienteAlergenos = (ingrediente.alergenos_ids || [])
                .map(id => alergenos.find(a => a.id === id))
                .filter(Boolean);
              
              return (
                <tr 
                  key={ingrediente.id} 
                  onClick={() => openEditModal(ingrediente)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-duetto-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                        <Package className="w-5 h-5 text-duetto-primary" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{ingrediente.nombre}</div>
                        <div className="text-sm text-gray-500">{ingrediente.unidad}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{getProveedorName(ingrediente.proveedor_id)}</div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900">€{ingrediente.precio_unitario?.toFixed(2) || '0.00'}</div>
                    <div className="text-xs text-gray-400">Con IVA: €{ingrediente.precio_unitario_con_iva?.toFixed(2) || '0.00'}</div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900">€{ingrediente.precio_paquete?.toFixed(2) || '0.00'}</div>
                    <div className="text-xs text-gray-400">Con IVA: €{ingrediente.precio_paquete_con_iva?.toFixed(2) || '0.00'}</div>
                    <div className="text-xs text-gray-500 mt-1">{ingrediente.unidades_por_paquete} uds · IVA {ingrediente.tipo_iva || 10}%</div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <div className="flex gap-1.5">
                      {ingrediente.carne && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" title="Contiene carne">
                          <Beef className="w-3 h-3 mr-1" />
                          Carne
                        </span>
                      )}
                      {ingrediente.origen_animal && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" title="Origen animal">
                          <Milk className="w-3 h-3 mr-1" />
                          Animal
                        </span>
                      )}
                      {ingrediente.pasteurizado && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="Pasteurizado">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Past.
                        </span>
                      )}
                      {!ingrediente.carne && !ingrediente.origen_animal && !ingrediente.pasteurizado && (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-4">
                    {ingredienteAlergenos.length > 0 ? (
                      <AlergenoList 
                        alergenos={ingredienteAlergenos}
                        size="sm"
                        maxDisplay={3}
                      />
                    ) : (
                      <span className="text-xs text-gray-400">Sin alérgenos</span>
                    )}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      ingrediente.activo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {ingrediente.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(ingrediente);
                        }}
                        className="text-duetto-primary hover:text-duetto-secondary"
                        title="Editar ingrediente"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(ingrediente.id);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar ingrediente"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredIngredientes.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No se encontraron ingredientes
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className={`p-6 border-b border-gray-200 ${editingIngrediente ? 'bg-duetto-primary bg-opacity-5' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editingIngrediente ? '✏️ Editar Ingrediente' : '➕ Nuevo Ingrediente'}
                  </h2>
                  {editingIngrediente && (
                    <p className="text-sm text-gray-600 mt-1">
                      Modificando: <span className="font-semibold">{editingIngrediente.nombre}</span>
                    </p>
                  )}
                </div>
                <button
                  onClick={resetForm}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="text-gray-500 text-2xl">×</span>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Basic Info Section */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="w-5 h-5 mr-2 text-duetto-primary" />
                  Información Básica
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre del Ingrediente *
                    </label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="Ej: Tomate maduro, Pechuga de pollo..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Proveedor</label>
                    <select
                      value={formData.proveedor_id}
                      onChange={(e) => setFormData({ ...formData, proveedor_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    >
                      <option value="">Sin proveedor asignado</option>
                      {[...proveedores].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(prov => (
                        <option key={prov.id} value={prov.id}>{prov.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unidad de Medida *</label>
                    <select
                      value={formData.unidad}
                      onChange={(e) => setFormData({ ...formData, unidad: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    >
                      <option value="kg">Kilogramos (kg)</option>
                      <option value="litros">Litros (L)</option>
                      <option value="unidad">Unidad</option>
                      <option value="gramos">Gramos (g)</option>
                      <option value="ml">Mililitros (ml)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="text-green-600 mr-2">💰</span>
                  Información de Precios (Sin IVA)
                </h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    ℹ️ Los precios se guardan <strong>SIN IVA</strong>. El sistema calcula automáticamente el precio con IVA según el tipo de IVA seleccionado ({formData.tipo_iva || 10}%).
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Precio Unitario Sin IVA (€) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.precio_unitario}
                      onChange={(e) => setFormData({ ...formData, precio_unitario: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="0.00"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Por {formData.unidad}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Precio Envase Sin IVA (€) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.precio_paquete}
                      onChange={(e) => setFormData({ ...formData, precio_paquete: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="0.00"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Precio por envase</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unidades por Envase *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={formData.unidades_por_paquete}
                      onChange={(e) => setFormData({ ...formData, unidades_por_paquete: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                      placeholder="1"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Cantidad en envase</p>
                  </div>
                </div>

                {/* Tipo de IVA */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de IVA *
                  </label>
                  <select
                    value={formData.tipo_iva || 10}
                    onChange={(e) => setFormData({ ...formData, tipo_iva: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    required
                  >
                    <option value="4">4% - Alimentos básicos</option>
                    <option value="10">10% - Alimentos preparados</option>
                    <option value="21">21% - General</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Con IVA {formData.tipo_iva || 10}%: Precio unitario = €{(parseFloat(formData.precio_unitario || 0) * (1 + (formData.tipo_iva || 10) / 100)).toFixed(2)} | Precio envase = €{(parseFloat(formData.precio_paquete || 0) * (1 + (formData.tipo_iva || 10) / 100)).toFixed(2)}
                  </p>
                </div>
                
                {/* Price Calculation Helper */}
                {formData.precio_paquete > 0 && formData.unidades_por_paquete > 0 && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-green-300">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Precio calculado por unidad:</span>{' '}
                      €{(parseFloat(formData.precio_paquete) / parseFloat(formData.unidades_por_paquete)).toFixed(2)} / {formData.unidad}
                    </p>
                  </div>
                )}
              </div>

              {/* Special Characteristics Section */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="w-5 h-5 mr-2 text-blue-600" />
                  Características Especiales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    formData.carne ? 'bg-red-100 border-red-400' : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.carne}
                      onChange={(e) => setFormData({ ...formData, carne: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                    />
                    <div className="flex items-center">
                      <Beef className="w-4 h-4 mr-2 text-red-600" />
                      <span className="text-sm font-medium text-gray-900">Contiene carne</span>
                    </div>
                  </label>

                  <label className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    formData.origen_animal ? 'bg-orange-100 border-orange-400' : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.origen_animal}
                      onChange={(e) => setFormData({ ...formData, origen_animal: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                    />
                    <div className="flex items-center">
                      <Milk className="w-4 h-4 mr-2 text-orange-600" />
                      <span className="text-sm font-medium text-gray-900">Origen animal</span>
                    </div>
                  </label>

                  <label className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    formData.pasteurizado ? 'bg-blue-100 border-blue-400' : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.pasteurizado}
                      onChange={(e) => setFormData({ ...formData, pasteurizado: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                    />
                    <div className="flex items-center">
                      <CheckCircle2 className="w-4 h-4 mr-2 text-blue-600" />
                      <span className="text-sm font-medium text-gray-900">Pasteurizado</span>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Estas características ayudan a identificar restricciones alimentarias
                </p>
              </div>

              {/* Allergens Section */}
              <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-amber-600" />
                  Alérgenos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {alergenos.map(alergeno => (
                    <label 
                      key={alergeno.id} 
                      className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-all ${
                        formData.alergenos_ids.includes(alergeno.id)
                          ? 'bg-amber-100 border-amber-400 shadow-sm ring-2 ring-amber-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.alergenos_ids.includes(alergeno.id)}
                        onChange={() => toggleAlergeno(alergeno.id)}
                        className="rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                      />
                      <AlergenoIcon nombre={alergeno.nombre} size="sm" />
                      <span className="text-xs font-medium text-gray-900">{alergeno.nombre}</span>
                    </label>
                  ))}
                </div>
                {formData.alergenos_ids.length > 0 && (
                  <div className="mt-3 p-2 bg-white rounded border border-amber-300">
                    <p className="text-xs text-gray-600 mb-1">Alérgenos seleccionados:</p>
                    <AlergenoList 
                      alergenos={formData.alergenos_ids.map(id => alergenos.find(a => a.id === id)).filter(Boolean)}
                      size="sm"
                      showLabels={false}
                    />
                  </div>
                )}
                {formData.alergenos_ids.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    ✓ Este ingrediente no contiene alérgenos registrados
                  </p>
                )}
              </div>

              {/* Status Section */}
              <div className="mb-6">
                <label className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.activo}
                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-900">Ingrediente activo</span>
                    <p className="text-xs text-gray-500">Los ingredientes inactivos no aparecerán en las recetas</p>
                  </div>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90 font-medium transition-colors flex items-center space-x-2"
                >
                  {editingIngrediente ? (
                    <>
                      <Edit className="w-4 h-4" />
                      <span>Actualizar Ingrediente</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>Crear Ingrediente</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ingredientes;
